import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { formatINR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Trash2, Minus, Plus } from "lucide-react";

export default function Cart() {
    const { items, updateQty, removeItem, subtotal } = useCart();
    const { user } = useAuth();
    const nav = useNavigate();

    const shipping = subtotal === 0 ? 0 : subtotal >= 999 ? 0 : 60;
    const total = subtotal + shipping;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="cart-page">
            <div className="label-eyebrow text-sky-700">Your basket</div>
            <h1 className="font-serif text-5xl text-stone-900 mt-2 mb-10">Cart</h1>

            {items.length === 0 ? (
                <div className="border border-stone-200 bg-white p-12 text-center">
                    <h3 className="font-serif text-2xl text-stone-900">Your cart is empty</h3>
                    <p className="text-sm text-stone-600 mt-2">Find your remedy in the pharmacy.</p>
                    <Link to="/shop"><Button className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50 mt-6">Browse the shop</Button></Link>
                </div>
            ) : (
                <div className="grid lg:grid-cols-3 gap-10">
                    <div className="lg:col-span-2 space-y-4">
                        {items.map((it) => (
                            <div key={`${it.product_id}-${it.size_id}`} className="bg-white border border-stone-200 p-4 flex gap-4" data-testid={`cart-item-${it.product_id}`}>
                                <div className="w-24 h-24 bg-stone-100 shrink-0">
                                    {it.image && <img src={it.image} alt={it.product_name} className="w-full h-full object-cover" />}
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <h3 className="font-serif text-lg text-stone-900">{it.product_name}</h3>
                                            <div className="text-xs text-stone-500 mt-1">{it.size_label}</div>
                                        </div>
                                        <button onClick={() => removeItem(it.product_id, it.size_id)} className="text-stone-400 hover:text-red-700" data-testid={`remove-${it.product_id}`}><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                    <div className="flex items-center justify-between mt-4">
                                        <div className="flex items-center border border-stone-300">
                                            <button onClick={() => updateQty(it.product_id, it.size_id, it.quantity - 1)} className="p-2 hover:bg-stone-50" aria-label="Decrease"><Minus className="w-3 h-3" /></button>
                                            <input
                                                type="number"
                                                min={1}
                                                value={it.quantity}
                                                onChange={(e) => {
                                                    const n = parseInt(e.target.value, 10);
                                                    if (Number.isFinite(n) && n >= 1) updateQty(it.product_id, it.size_id, n);
                                                    else if (e.target.value === "") updateQty(it.product_id, it.size_id, 1);
                                                }}
                                                onBlur={(e) => { if (!e.target.value || parseInt(e.target.value, 10) < 1) updateQty(it.product_id, it.size_id, 1); }}
                                                className="w-12 text-center text-sm bg-transparent outline-none border-x border-stone-200 py-1"
                                                data-testid={`cart-qty-${it.product_id}-${it.size_id}`}
                                                aria-label="Quantity"
                                            />
                                            <button onClick={() => updateQty(it.product_id, it.size_id, it.quantity + 1)} className="p-2 hover:bg-stone-50" aria-label="Increase"><Plus className="w-3 h-3" /></button>
                                        </div>
                                        <div className="font-serif text-lg text-stone-900">{formatINR(it.price * it.quantity)}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <aside className="bg-white border border-stone-200 p-6 h-fit" data-testid="cart-summary">
                        <div className="label-eyebrow text-stone-500">Order summary</div>
                        <div className="mt-4 space-y-3 text-sm">
                            <div className="flex justify-between"><span className="text-stone-600">Subtotal</span><span>{formatINR(subtotal)}</span></div>
                            <div className="flex justify-between"><span className="text-stone-600">Shipping</span><span>{shipping === 0 ? "Free" : formatINR(shipping)}</span></div>
                            <div className="flex justify-between border-t border-stone-200 pt-3 font-serif text-xl text-stone-900"><span>Total</span><span>{formatINR(total)}</span></div>
                        </div>
                        <Button
                            onClick={() => nav(user ? "/checkout" : "/login?next=/checkout")}
                            className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50 w-full mt-6 py-6"
                            data-testid="checkout-btn"
                        >
                            Proceed to checkout
                        </Button>
                        {subtotal < 999 && <p className="text-xs text-stone-500 mt-3">Free shipping on orders ≥ {formatINR(999)}.</p>}
                    </aside>
                </div>
            )}
        </div>
    );
}
